/**
 * Apply the server-minted intake Patient session to the global Medplum client.
 *
 * The intake-email-service bot performs the password login + token exchange
 * server-side and returns only the resulting session (access/refresh token). The
 * browser never receives or replays a password — it simply installs the session,
 * replacing the anonymous `client_credentials` login with the Patient.
 */

import type { MedplumClient } from '@medplum/core';

/** Mirror of the bot's `MintedPatientLogin` (intake-email-service.ts). */
export interface MintedPatientLogin {
  accessToken: string;
  refreshToken: string;
  /** e.g. `Patient/{id}` */
  profile: string;
  /** e.g. `Project/{id}` */
  project: string;
}

interface ActiveMedplumLogin {
  accessToken: string;
  refreshToken: string;
  profile: { reference: string };
  project: { reference: string };
}

function toActiveLogin(login: MintedPatientLogin): ActiveMedplumLogin {
  return {
    accessToken: login.accessToken,
    refreshToken: login.refreshToken,
    profile: { reference: login.profile },
    project: { reference: login.project },
  };
}

/** Replace the global Medplum session with the minted Patient session. */
export async function applyIntakePatientSession(
  medplum: MedplumClient,
  login: MintedPatientLogin,
): Promise<void> {
  if (!login?.accessToken || !login?.refreshToken || !login?.profile || !login?.project) {
    throw new Error('Intake session is missing required credentials.');
  }
  await medplum.setActiveLogin(toActiveLogin(login));
}
